import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { optimizely } from 'Optimizely'
import { formatRegistrationStep } from 'util/registrationFlow'
import { basicInfoSelector } from 'selectors'
import { Container, Text } from 'roadie-ui'
import { addBusinessInfo, updateProfile } from 'actions/profile'
import styled from 'styled-components/macro'
import Page from 'rui/widgets/Page'
import DriveSidebar from 'components/layout/DriveSidebar'
import Thin from 'components/layout/Thin'
import Panel, { PanelHeading, PanelSubheading, PanelTitle } from 'components/layout/Panel'
import Form from 'components/form'
import { required, minLength, legalAdult, stillLiving } from 'components/form/validations'
import { Body1 } from 'components/layout/text'
import Input from 'components/form/Input'
import Step from 'components/misc/Step'
import Spacer from 'components/misc/Spacer'
import { ExternalLink } from 'components/layout/links'
import { states } from 'types/states'
import { createAddressValidation } from 'util/server/mainApi'
import { allowedRegexBusinessName, handleBusinessNameKeyDown, handleBusinessNamePaste } from './BusinessInfo'
import { LocalizedRichText, useLocalization, LocalizedText } from 'localization'

const StateAndZip = styled.div`
  display: flex;

  > :first-child,
  > :last-child {
    width: 100%;
    flex-grow: 1;
  }
`

const StateAndZipDivider = styled.div`
  width: 1.5rem;
  flex-shrink: 0;
  flex-grow: 0;
`

// TODO: Refactor this component to use the roadie-ui Form component.
// You will need to rewrite the address lookup input functionality
export const PersonalInfoForm = ({ profile, registered, isLocked, dispatch }) => {
  const { t } = useLocalization()
  const [fullAddress, setFullAddress] = useState(profile && profile.preferred_address)
  const [noMiddle, setNoMiddle] = useState(
    profile.certifications.includes('basic_driver') ? false : !profile.middle_name
  )
  const [hasBusiness, setHasBusiness] = useState(false)
  const verifyAddressFF = optimizely.decide('drive_verify_address')
  const isFormFieldDisabled = verifyAddressFF.enabled ? true : isLocked
  const registrationStep = formatRegistrationStep('BASIC_INFO', profile)

  const handleAddress = (selectedAddress) => {
    // Populate address fields with the selected address else clear them if field is empty
    const fullAddress = {
      street: selectedAddress.street || '',
      apt_number: selectedAddress.apt_number || '',
      city: selectedAddress.city || '',
      state: selectedAddress.state || '',
      zip: selectedAddress.zip || '',
      latitude: selectedAddress.latitude,
      longitude: selectedAddress.longitude,
      friendly_name: selectedAddress.friendly_name,
      text: selectedAddress.text,
    }
    setFullAddress(fullAddress)
  }

  const handleHasMiddle = (event) => {
    setNoMiddle(event.target.checked)
  }

  const handleHasBusiness = (event) => {
    setHasBusiness(event.target.value === 'yes' ? true : false)
  }

  const handleSubmit = async (data) => {
    return createAddressValidation(data).then(
      async () => {
        const profileSource = registered ? profile : data

        const { first_name, last_name, date_of_birth: dob } = profileSource
        const { street, apt_number, city, state, zip } = data
        const { has_business } = data

        const middle_name = registered ? profile.middle_name : data.no_middle ? '' : data.middle_name

        let formated

        if (registered) {
          formated = {
            profile: {
              first_name,
              middle_name,
              last_name,
              street_address: street,
              preferred_address: {
                street,
                apt_number,
                city,
                state,
                zip,
              },
            },
          }
        } else {
          const [mm, dd, yyyy] = dob.split('/')
          const date_of_birth = `${yyyy}-${mm}-${dd}`

          formated = {
            profile: {
              first_name,
              middle_name,
              last_name,
              date_of_birth,
              street_address: street,
              preferred_address: {
                street,
                apt_number,
                city,
                state,
                zip,
              },
            },
          }
        }

        try {
          if (has_business === 'yes') {
            const { business_name, business_state, ein } = data
            if (!allowedRegexBusinessName.test(business_name)) {
              dispatch({
                type: 'PUSH_FLASH',
                flash: {
                  type: 'error',
                  messageKey: 'dweb_flash_business_name_allowed_characters',
                },
              })
              return
            }
            await addBusinessInfo(business_name, business_state, ein, false)(dispatch)
          }
          await updateProfile(formated)(dispatch).then(async () => {
            if (registered) {
              dispatch({
                type: 'PUSH_FLASH',
                flash: {
                  messageKey: 'dweb_flash_profile_changes_saved',
                  type: 'success',
                },
              })
            }
          })
        } catch (err) {
          const errors = err.response.data.errors
          const errorType = Object.keys(errors)[0]
          switch (errorType) {
            case 'business_name':
              dispatch({ type: 'PUSH_FLASH', flash: { type: 'error', messageKey: 'dweb_flash_business_name_invalid' } })
              break
            case 'ein':
              if (errors['ein'][0] == 'already exists') {
                dispatch({
                  type: 'PUSH_FLASH',
                  flash: {
                    type: 'error',
                    messageKey: 'dweb_flash_ein_already_exists_single_member_only',
                  },
                })
              } else {
                dispatch({ type: 'PUSH_FLASH', flash: { type: 'error', messageKey: 'dweb_flash_ein_invalid' } })
              }
              break
            default:
              dispatch({
                type: 'PUSH_FLASH',
                flash: { type: 'error', messageKey: 'dweb_flash_generic_error_occurred' },
              })
          }
        }
      },
      () => {
        return Promise.reject({
          response: {
            data: {
              errors: {
                base: t('dweb_validation_address_not_valid_try_again_or_contact_support_assistance'),
              },
            },
          },
        })
      }
    )
  }

  return (
    <Form id="basic-info-form" onData={handleSubmit}>
      <PanelSubheading>
        <LocalizedText translationKey="dweb_basic_info_registration_license_exact_match" />
      </PanelSubheading>
      <Input.Name
        label={t('dweb_account_basic_info_registration_first_name')}
        name="first_name"
        validate={required}
        disabled={registered || isLocked}
        defaultValue={profile.first_name}
      />
      <Input.Name
        key={noMiddle ? 'noMiddle' : 'yesMiddle'}
        label={t('dweb_account_basic_info_registration_middle_name')}
        name="middle_name"
        disabled={registered || noMiddle || isLocked}
        validate={noMiddle ? undefined : required}
        defaultValue={noMiddle ? '' : profile.middle_name || ''}
      />
      <Input.Checkbox
        label={<LocalizedText translationKey="dweb_account_basic_info_registration_i_dont_have_middle_name" />}
        name="no_middle"
        disabled={registered || isLocked}
        defaultChecked={noMiddle}
        onChange={handleHasMiddle}
      />
      <Input.Name
        label={t('dweb_account_basic_info_registration_last_name')}
        name="last_name"
        disabled={registered || isLocked}
        validate={minLength(2).required()}
        defaultValue={profile.last_name}
      />
      {registered ? null : (
        <Input.Date
          label={t('dweb_account_basic_info_registration_date_birth')}
          disabled={registered || isLocked}
          validate={legalAdult.and(stillLiving).required()}
          name="date_of_birth"
        />
      )}
      {registered && (
        <>
          <Spacer />
          <Body1>
            <LocalizedRichText
              translationKey="dweb_account_basic_info_registration_update_information_please_contact_link1"
              params={{
                link1: (
                  <ExternalLink href={`${import.meta.env.REACT_APP_SUPPORT_URL}/support/s/contactsupport`}>
                    <LocalizedText translationKey="dweb_account_basic_info_registration_roadie_support" />
                  </ExternalLink>
                ),
              }}
            />
          </Body1>
        </>
      )}
      <Spacer />
      <Input.Address
        className="fs-block"
        name="street"
        label={t('dweb_account_basic_info_registration_street_address')}
        defaultValue={fullAddress && fullAddress.street}
        validate={required}
        onAddress={handleAddress}
        displayField="street"
        disabled={isLocked}
      />
      <Input
        key={fullAddress ? fullAddress.apt_number : 'empty-apt'}
        className="fs-block"
        label={t('dweb_account_basic_info_registration_apt_suite_bldg_optional')}
        defaultValue={fullAddress && fullAddress.apt_number}
        maxLength={25}
        name="apt_number"
        disabled={isLocked}
      />
      <Input
        key={fullAddress ? fullAddress.city : 'empty-city'}
        className="fs-block"
        label={t('dweb_form_validate_address_city')}
        name="city"
        validate={required}
        defaultValue={fullAddress && fullAddress.city}
        disabled={isFormFieldDisabled}
      />
      <StateAndZip>
        <Input.Select
          key={fullAddress ? fullAddress.state : 'empty-state'}
          className="fs-block"
          label={t('dweb_account_driving_info_vehicle_info_state')}
          name="state"
          validate={required}
          defaultValue={fullAddress && fullAddress.state}
          disabled={isFormFieldDisabled}
        >
          {states.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </Input.Select>
        <StateAndZipDivider />
        <Input.Integer
          key={fullAddress ? fullAddress.zip : 'empty-zip'}
          className="fs-block"
          type="tel"
          kind="zip"
          label={t('dweb_form_validate_address_zip')}
          name="zip"
          maxLength={5}
          validate={minLength(5).withMessage(t('dweb_validation_must_be_exactly_5_digits')).required()}
          defaultValue={fullAddress && fullAddress.zip}
          disabled={isFormFieldDisabled}
        />
      </StateAndZip>

      {!registered && (
        <>
          <Spacer />
          <Container.Base $textAlign="left" $padding="12px 0 0 0">
            <PanelTitle>
              <LocalizedText translationKey="dweb_account_basic_info_registration_business_info" />
            </PanelTitle>
          </Container.Base>
          <Text.Body2 $color="neutral-black" $textAlign="start">
            <LocalizedRichText
              translationKey="dweb_basic_info_registration_business_type_question"
              params={{
                strongText1: (
                  <strong>
                    <LocalizedText translationKey="dweb_account_basic_info_registration_not_required" />
                  </strong>
                ),
              }}
            />
          </Text.Body2>
          <Input.RadioGroup
            name="has_business"
            onChange={handleHasBusiness}
            disabled={isLocked}
            defaultValue={hasBusiness ? 'yes' : 'no'}
          >
            {(radioElementProps) => (
              <>
                <Input.RadioElement
                  value="no"
                  label={<LocalizedText translationKey="dweb_account_basic_info_registration_no_im_not_sure" />}
                  disabled={isLocked}
                  {...radioElementProps}
                />
                <Input.RadioElement
                  value="yes"
                  label={<LocalizedText translationKey="dweb_common_yes" />}
                  disabled={isLocked}
                  {...radioElementProps}
                />
              </>
            )}
          </Input.RadioGroup>
          {hasBusiness ? (
            <Container.Base>
              <Spacer />
              <Input.Text
                label={t('dweb_account_basic_info_registration_business_name')}
                name="business_name"
                validate={required}
                disabled={isLocked}
                onKeyDown={handleBusinessNameKeyDown}
                onPaste={handleBusinessNamePaste}
                maxLength={40}
              />
              <StateAndZip>
                <Input.Select
                  className="fs-block"
                  label={t('dweb_account_basic_info_registration_state_registration')}
                  name="business_state"
                  validate={required}
                  defaultValue={fullAddress && fullAddress.state}
                  disabled={isLocked}
                >
                  {states.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </Input.Select>
                <StateAndZipDivider />
                <Input.EIN
                  className="fs-block"
                  label={t('dweb_account_basic_info_registration_ein')}
                  name="ein"
                  validate={required}
                  disabled={isLocked}
                />
              </StateAndZip>
              <Spacer />
              <Text.Body2 $color="neutral-black" $textAlign="start">
                <LocalizedText translationKey="dweb_basic_info_registration_ein_verification_notice" />
              </Text.Body2>
            </Container.Base>
          ) : (
            <></>
          )}
        </>
      )}

      <Input.Submit value={!registered ? t('dweb_common_next') : t('dweb_common_save')} disabled={isLocked} />
      <Spacer />
      {!registered && registrationStep && <Step>{registrationStep}</Step>}
    </Form>
  )
}

const BasicInfoRegistration = () => {
  const { t } = useLocalization()
  const { registered, profile, isLocked } = useSelector(basicInfoSelector)
  const dispatch = useDispatch()

  return (
    <Page sidebar={!registered ? undefined : <DriveSidebar />}>
      <Helmet>
        <title>{t('dweb_layout_drive_sidebar_basic_info')}</title>
      </Helmet>
      <Thin>
        <Panel>
          <PanelHeading>
            <LocalizedText translationKey="dweb_account_basic_info_registration_basic_information" />
          </PanelHeading>
          <PersonalInfoForm profile={profile} dispatch={dispatch} registered={registered} isLocked={isLocked} />
        </Panel>
      </Thin>
    </Page>
  )
}

export default BasicInfoRegistration
